"use client";

import { useRef, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Sparkles, 
  TrendingUp, 
  Megaphone, 
  Award 
} from 'lucide-react';
import PropertyCard, { PropertyCardProps } from './PropertyCard';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Loading from '@/components/app/loading/LoadingSpinner';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Star, ArrowLeft, ArrowRight } from "lucide-react";
import { debounce } from "@/lib/utils";

interface FeaturedCarouselProps {
  title: string;
  subtitle?: string;
  properties: PropertyCardProps['property'][];
  type: 'featured' | 'sponsored';
  className?: string;
}

const FeaturedCarousel = ({ 
  title, 
  subtitle,
  properties, 
  type, 
  className = '' 
}: FeaturedCarouselProps) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  
  // Define the icon and color scheme based on type
  const carouselTheme = {
    featured: {
      icon: Award,
      iconBg: 'bg-indigo-100',
      iconText: 'text-indigo-600',
      titleText: 'text-indigo-700',
      gradientLine: 'bg-gradient-to-r from-indigo-200 to-transparent',
      activeDot: 'bg-indigo-500',
      buttonBorder: 'border-indigo-200',
      buttonText: 'text-indigo-600',
      cardBg: 'bg-gradient-to-br from-indigo-50 to-indigo-100',
      cardBorder: 'border-indigo-200',
      cardCircleBg: 'bg-indigo-100',
      cardIconText: 'text-indigo-500',
      cardTitleText: 'text-indigo-700',
      cardButtonBg: 'bg-indigo-600',
      cardButtonHover: 'hover:bg-indigo-700'
    },
    sponsored: {
      icon: Megaphone,
      iconBg: 'bg-pink-100',
      iconText: 'text-pink-600',
      titleText: 'text-pink-700',
      gradientLine: 'bg-gradient-to-r from-pink-200 to-transparent',
      activeDot: 'bg-pink-500',
      buttonBorder: 'border-pink-200',
      buttonText: 'text-pink-600',
      cardBg: 'bg-gradient-to-br from-pink-50 to-pink-100',
      cardBorder: 'border-pink-200',
      cardCircleBg: 'bg-pink-100',
      cardIconText: 'text-pink-500',
      cardTitleText: 'text-pink-700',
      cardButtonBg: 'bg-pink-600',
      cardButtonHover: 'hover:bg-pink-700'
    }
  }[type];
  
  const Icon = carouselTheme.icon;
  
  // Check if carousel can scroll
  const checkScrollability = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };
  
  // Initialize scroll checks
  useEffect(() => {
    if (typeof window !== "undefined") {
      checkScrollability();
      const debouncedCheckScrollability = debounce(checkScrollability, 16); // ~60fps throttling
      window.addEventListener("resize", debouncedCheckScrollability);
    
      return () => {
        window.removeEventListener("resize", debouncedCheckScrollability);
      };
    }
  }, [properties]);
  
  // Handle scroll events
  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      const handleScroll = debounce(() => {
        checkScrollability();
        
        // Calculate active index based on scroll position
        if (carousel.scrollWidth > carousel.clientWidth) {
          const scrollPercentage = carousel.scrollLeft / (carousel.scrollWidth - carousel.clientWidth);
          const itemCount = properties.length;
          const newIndex = Math.min(
            Math.floor(scrollPercentage * itemCount),
            itemCount - 1
          );
          
          setActiveIndex(Math.max(0, newIndex));
        }
      }, 16); // ~60fps throttling
      
      carousel.addEventListener('scroll', handleScroll);
      return () => carousel.removeEventListener('scroll', handleScroll);
    }
  }, [properties.length]);
  
  // Scroll the carousel
  const scroll = (direction: 'left' | 'right') => {
    if (carouselRef.current && !isAnimating) {
      setIsAnimating(true);
      
      const { scrollLeft, clientWidth } = carouselRef.current;
      const scrollAmount = Math.floor(clientWidth * 0.75);
      const newScrollLeft = direction === 'left' 
        ? Math.max(0, scrollLeft - scrollAmount)
        : scrollLeft + scrollAmount;
      
      carouselRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
      
      // Reset animation lock after scroll completes
      setTimeout(() => setIsAnimating(false), 500);
    }
  };
  
  // Scroll to a specific index
  const scrollToIndex = (index: number) => {
    if (carouselRef.current && !isAnimating) {
      setIsAnimating(true);
      setActiveIndex(index);
      
      const itemWidth = carouselRef.current.scrollWidth / properties.length;
      const newScrollLeft = itemWidth * index;
      
      carouselRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
      
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  // Handle sponsored properties navigation with loading
  const handleSponsoredNavigation = () => {
    setIsLoading(true);
    router.push("/listings/sponsored");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.1 }}
      className={`relative ${className}`}
    >
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <span className={`p-1.5 rounded-lg ${carouselTheme.iconBg} ${carouselTheme.iconText}`}>
            <Icon className="h-5 w-5" />
          </span>
          <h2 className={`text-xl md:text-2xl font-bold ${carouselTheme.titleText}`}>{title}</h2>
          <div className={`h-px flex-1 ${carouselTheme.gradientLine}`}></div>
        </div>
        
        {subtitle && (
          <p className="text-gray-600 text-sm md:text-base max-w-3xl">
            {subtitle}
          </p>
        )}
      </div>
      
      {/* Carousel Navigation */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1">
          {properties.length > 1 && properties.slice(0, 5).map((_, idx) => (
            <button
              key={idx}
              onClick={() => scrollToIndex(idx)}
              className={`
                w-2 h-2 rounded-full transition-all duration-300 
                ${activeIndex === idx 
                  ? `${carouselTheme.activeDot} scale-125` 
                  : 'bg-gray-300 hover:bg-gray-400'}
              `}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
        
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => scroll('left')}
            disabled={!canScrollLeft || isAnimating}
            className={`
              p-1 h-8 w-8 rounded-full ${carouselTheme.buttonBorder}
              ${!canScrollLeft ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            aria-label="Scroll left"
          >
            <ChevronLeft className={`h-5 w-5 ${carouselTheme.buttonText}`} />
          </Button>
          
          <Button
            size="sm"
            variant="outline"
            onClick={() => scroll('right')}
            disabled={!canScrollRight || isAnimating}
            className={`
              p-1 h-8 w-8 rounded-full ${carouselTheme.buttonBorder}
              ${!canScrollRight ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            aria-label="Scroll right"
          >
            <ChevronRight className={`h-5 w-5 ${carouselTheme.buttonText}`} />
          </Button>
        </div>
      </div>
      
      {/* Carousel */}
      <div
        ref={carouselRef}
        className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide"
        style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
        onScroll={checkScrollability}
      >
        {properties.map((property, index) => (
          <PropertyCard
            key={property.id}
            property={property}
            variant="carousel"
            animationDelay={index * 0.05}
          />
        ))}
        
        {/* View all card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            type: 'spring',
            stiffness: 100, 
            damping: 15,
            delay: properties.length * 0.05 
          }}
          className="min-w-[300px] max-w-xs shrink-0"
        >
          <div className={`
            h-full flex flex-col justify-center items-center p-6 rounded-xl
            ${carouselTheme.cardBg}
            border ${carouselTheme.cardBorder} shadow-sm
          `}>
            <div className={`
              mb-4 w-16 h-16 rounded-full ${carouselTheme.cardCircleBg} 
              flex items-center justify-center
            `}>
              <Sparkles className={`h-8 w-8 ${carouselTheme.cardIconText}`} />
            </div>
            
            <h3 className={`font-bold text-xl ${carouselTheme.cardTitleText} mb-2 text-center`}>
              View All {type === 'featured' ? 'Featured' : 'Sponsored'} Properties
            </h3>
            
            <p className="text-gray-600 text-sm text-center mb-6">
              Discover our full collection of premium {type === 'featured' ? 'featured' : 'sponsored'} properties.
            </p>
            
            {type === 'sponsored' ? (
              <Button 
                onClick={handleSponsoredNavigation}
                className={`
                  ${carouselTheme.cardButtonBg} ${carouselTheme.cardButtonHover} text-white w-full
                `}
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Browse All
              </Button>
            ) : (
              <Button 
                className={`
                  ${carouselTheme.cardButtonBg} ${carouselTheme.cardButtonHover} text-white w-full
                `}
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Browse All
              </Button>
            )}
          </div>
        </motion.div>
      </div>
      
      {/* Gradient fades on edges for better UX */}
      {canScrollLeft && (
        <div className={`
          absolute left-0 top-0 bottom-0 w-8 
          bg-gradient-to-r from-white to-transparent 
          pointer-events-none
        `} />
      )}
      
      {canScrollRight && (
        <div className={`
          absolute right-0 top-0 bottom-0 w-8 
          bg-gradient-to-l from-white to-transparent 
          pointer-events-none
        `} />
      )}
      
      {/* Global loading overlay */}
      {isLoading && <Loading />}
    </motion.div>
  );
};

export default FeaturedCarousel; 